/**
 * Glue layer between game state, Rive layers, Pixi chip animations, and the
 * DOM result panel. Mirrors `_GameScreenState` of
 *   packages/amgsicbo_v2/lib/presentation/screens/game_screen.dart.
 *
 * Input flow (driven by Rive events emitted from the betting UI artboard):
 *   placeBetClick {betID}    → tryPlaceBet  (uses last tap position for chip drop)
 *   evtClickChips {chipIndex}→ selectChip
 *   evtClearClick            → clearAll
 *   evtButtonPlayClick       → confirm  (single-player only)
 *
 * Output flow (driven by phase transitions + animation-end events):
 *   GamePhase.betting        → girlAnim(1), playDiceBowlStart, show startBet panel
 *   GamePhase.diceRoll       → revealDiceBowl(d1,d2,d3) over 1800ms
 *   Rive evtResultStop        → girlAnim(2) + start result sequence
 *     t=2s: chip animation by winMode (player/dealer/half-half)
 *     t=3s: phase=showResult (win/lose panel shown via state flags)
 *     t=11s: safety-net reset → new round
 */
import type { BetType, GameRound } from '../types/game';
import { betTypeById } from '../types/game';
import {
  DEFAULT_AGENT_ID,
  DEFAULT_GAME_CONFIG,
  DEFAULT_PASSWORD,
  DICE_REVEAL_DURATION_MS,
  GAME_KEY_MULTI,
  GAME_KEY_SINGLE,
  REF_HEIGHT,
  REF_WIDTH,
} from '../config';
import type { Api } from '../net/api';
import { ApiError } from '../net/api';
import type { GameState, GameStateSnapshot } from './gameState';
import type { RiveLayer } from '../rive/riveLayer';
import type { ChipLayer } from '../pixi/chipLayer';
import type { Hud } from '../ui/hud';
import type { TableControls } from '../ui/controls';
import type { UrlParams } from './urlParams';
import type { MultiplayerHub, HubEvent } from '../net/multiplayerHub';
// showModeSelectionDialog disabled — the mode now comes from the launch URL
// (g param / path). Kept importable from '../ui/modeSelectionDialog' if re-enabled.
import { type GameMode } from '../ui/modeSelectionDialog';
import type { GameTimerWidget } from '../ui/timerWidget';
import type { GameResultPanel } from '../ui/gameResultPanel';
import { parseResultString, type ParsedResult } from './resultParser';
import { formatDiceResult, winningBetIds } from './winningBets';
import { GameLogManager } from './gameLogManager';
import { debugLog, error, warn } from '../util/logger';

/**
 * Multiplayer only: end the visible bet countdown this many seconds before
 * the server's real betting-window close. The batched bet submission fires
 * when the countdown reaches 0, so this buffer gives it time to reach the
 * backend before betting closes and the round starts playing.
 */
const BET_SUBMIT_BUFFER_SEC = 2;

interface OrchestratorDeps {
  state: GameState;
  api: Api;
  rive: RiveLayer;
  chips: ChipLayer;
  hud: Hud;
  controls: TableControls;
  hub: MultiplayerHub;
  timer: GameTimerWidget;
  resultPanel: GameResultPanel;
  params: UrlParams;
  /** Returns the last pointer-down position in 1792×828 stage coords. */
  getLastTap: () => { x: number; y: number } | null;
  onInteractive?: () => void;
}

export class GameOrchestrator {
  private readonly d: OrchestratorDeps;
  private gid: string = '';

  // Result-sequence guard flags (mirror _didStartDiceRoll / _didStartResultSequence
  // / _didResetAfterResult in game_screen.dart). Reset every round.
  private didStartDiceRoll = false;
  private didStartResultSequence = false;
  private didResetAfterResult = false;
  private didHandleResultStop = false;
  // Guards the one-shot YOU WIN / YOU LOSE panel reveal (revealWinLosePanel).
  private didRevealWinLose = false;
  // Balance settlement is deferred to the YOU WIN / YOU LOSE reveal so the
  // displayed (available) balance stays "bet deducted" through the dice
  // reveal and only reflects winnings once the result is shown.
  private pendingSettleBalance: number | null = null;
  private didSettle = false;
  // Multiplayer: the result reveal reached the settle point but the server's
  // Result event (which carries the post-round balance) hasn't arrived yet.
  // We hold the bets in place until it does (see applySettlement) so the
  // displayed available balance never flashes the pre-settlement wallet.
  private awaitingSettleBalance = false;
  // Bumped each round start; settlement captures it before the async balance
  // fetch and bails if a new round started meanwhile.
  private roundToken = 0;
  // Guards applyResultSettlement to once per round.
  private didApplyResultSettlement = false;
  // Multiplayer bets are NOT sent per-chip; they're batched and submitted
  // when the bet countdown ends, so the player can still clear beforehand.
  private didSubmitMpBets = false;
  private sequenceTimers: number[] = [];

  // Multiplayer timers.
  private betCountdownHandle: number | null = null;
  private restartCountdownHandle: number | null = null;
  private lastMpEventIndex: number | null = null;

  constructor(deps: OrchestratorDeps) {
    this.d = deps;
  }

  async start(): Promise<void> {
    this.d.state.on((next, prev) => this.onStateChange(next, prev));
    this.bindRiveEvents();
    this.bindHubEvents();
    this.d.controls.bindChips(this.d.state.config);
    this.d.controls.setSelectedChip(this.d.state.selectedChipIndex);
    this.d.controls.setControlsEnabled(false, false);

    this.d.hud.setUserName(this.d.params.userName);
    this.d.rive.setUsernameText(this.d.params.userName);
    this.d.state.setPhase('initializing');
    this.d.hud.setStatus('Connecting…');

    try {
      await this.signIn();
      await this.loadConfigAndBalance();
      this.d.onInteractive?.();

      // Mode selection dialog disabled — the mode is now derived from the
      // launch URL the lobby routes to (single `/?g=SBA1` vs multi
      // `/sekbaohub?g=SBAM`; see urlParams.resolveGameMode) instead of
      // prompting the player at launch. Original dialog code kept below
      // (commented) for reference.
      //
      // this.d.state.setPhase('modeSelection');
      // this.d.hud.setStatus('Select mode');
      // const mode: GameMode = await showModeSelectionDialog();
      // debugLog('[Orchestrator] mode selected:', mode);
      const mode: GameMode = this.d.params.gameMode;
      debugLog('[Orchestrator] mode from launch URL (g/path):', mode);

      if (mode === 'multi') {
        this.d.controls.setPlayClearHidden(true);
        await this.startMultiplayer();
      } else {
        this.d.controls.setPlayClearHidden(false);
        this.startBettingRound();
      }
    } catch (e) {
      error('[Orchestrator] start failed:', e);
      this.d.state.setError(extractMessage(e));
      this.d.hud.setStatus('ERROR: ' + extractMessage(e));
    }
  }

  // =================================================================
  // Bootstrap
  // =================================================================

  private async signIn(): Promise<void> {
    const { params } = this.d;
    const agentId = params.agentId || DEFAULT_AGENT_ID;

    let bearerToken: string;
    let gsid: string;

    if (params.token && params.gsid) {
      bearerToken = ensureUrlEncoded(params.token);
      gsid = params.gsid;
    } else {
      this.d.hud.setStatus('Logging in…');
      const login = await this.d.api.login({
        userName: params.userName,
        agentId,
        password: DEFAULT_PASSWORD,
      });
      if (!login.sessionId) throw new Error('Login response missing SessionId');

      this.d.api.setCredentials({
        sessionToken: login.sessionId,
        gameSessionId: null,
        agentId: login.agentId || agentId,
        userName: params.userName,
      });

      this.d.hud.setStatus('Opening game…');
      const session = await this.d.api.openGame({
        userName: params.userName,
        agentId: login.agentId || agentId,
        gameKey: GAME_KEY_SINGLE,
      });

      const parsed = parseGameUrl(session.currentGameUrl, login.sessionId);
      bearerToken = ensureUrlEncoded(parsed.gameToken);
      gsid = parsed.gsid || session.sessionId;

      this.d.state.setBalance(session.balance);
      this.applyServerConfig(session.chips, session.minBet, session.maxBet);
    }

    this.d.api.setCredentials({
      sessionToken: bearerToken,
      gameSessionId: gsid,
      agentId,
      userName: params.userName,
    });
    this.gid = gsid;

    this.d.hud.setStatus('Fetching balance…');
    try {
      const balance = await this.d.api.checkBalance({ sessionId: gsid });
      this.d.state.setBalance(balance);
    } catch (e) {
      warn('[Orchestrator] checkBalance failed (continuing):', e);
    }
  }

  private async loadConfigAndBalance(): Promise<void> {
    const sessionId = this.d.api.getCredentials().gameSessionId ?? '';
    try {
      const info = await this.d.api.getInfo({ sessionId });
      if (info.balance > 0) this.d.state.setBalance(info.balance);
      this.applyServerConfig(info.chips, info.minBet, info.maxBet);
    } catch (e) {
      warn('[Orchestrator] getInfo failed (continuing with defaults):', e);
      if (this.d.state.config.chipValues.length === 0) {
        this.d.state.setConfig({
          minBet: DEFAULT_GAME_CONFIG.minBet,
          maxBet: DEFAULT_GAME_CONFIG.maxBet,
          chipValues: [...DEFAULT_GAME_CONFIG.chipValues],
        });
      }
    }
  }

  private applyServerConfig(chips: number[], minBet: number, maxBet: number): void {
    const current = this.d.state.config;
    const nextChips = chips.length > 0 ? chips : [...current.chipValues];
    const nextMin = minBet > 0 ? minBet : current.minBet;
    const nextMax = maxBet > 0 ? maxBet : current.maxBet;
    this.d.state.setConfig({
      minBet: nextMin,
      maxBet: nextMax,
      chipValues: nextChips,
    });
  }

  // =================================================================
  // Rive event bindings
  // =================================================================

  private bindRiveEvents(): void {
    this.d.rive.on('placeBetClick', (payload) => {
      const id = (payload.betID ?? payload.betId ?? payload.id) as
        | number
        | undefined;
      if (typeof id !== 'number') return;
      const bet = betTypeById(id);
      if (bet) this.tryPlaceBet(bet);
    });

    this.d.rive.on('evtClickChips', (payload) => {
      const i = (payload.chipIndex ?? payload.index) as number | undefined;
      if (typeof i === 'number') this.d.state.selectChip(i);
    });

    this.d.rive.on('evtClearClick', () => this.clearAll());

    this.d.rive.on('evtButtonPlayClick', () => void this.confirm());

    this.d.rive.on('UIButtonClick', (payload) => {
      const parts = String(payload.parts ?? '');
      if (parts === 'startBetEnd') {
        // startBet panel is DOM in V2; this Rive callback is a no-op.
      } else if (parts === 'noMoreBet_End') {
        // Hide the noMoreBet panel and kick off the dice roll.
        this.d.state.setShowNoMoreBetPanel(false);
        this.d.resultPanel.hide();
        if (this.didStartDiceRoll) return;
        const round = this.d.state.currentRound;
        if (!round) return;
        this.didStartDiceRoll = true;
        void this.playDiceRollSequence(round);
      }
    });

    // NOTE: we deliberately do NOT listen to the Rive `evtResultStop` /
    // `DiceBowlEnd` events to reveal YOU WIN / YOU LOSE. Those fire when the
    // dice bowl finishes opening, but at that instant the server's settlement
    // (the authoritative win/lose AMOUNT) has not arrived yet — verified in the
    // console: `evtResultStop received … totalWinLose= 0`. So the reveal is
    // driven by Result arrival instead: handleResult (multiplayer) and the
    // dice-roll completion in single-player (where confirm() already knows the
    // amount). See revealResult().
  }

  /**
   * Reveal the round result: girl reaction (2) + YOU WIN / YOU LOSE panel + the
   * chip-animation / reset sequence. Idempotent per round.
   *
   * Called when the settled win/lose amount is known:
   *   - multiplayer → on Result arrival (handleResult), the authoritative amount
   *   - single-player → when the dice settle (confirm() already has the amount)
   */
  private revealResult(net: number, positiveWin: number): void {
    if (!this.didHandleResultStop) {
      this.didHandleResultStop = true;
      this.d.rive.triggerGirlAnim(2);
    }
    this.revealWinLosePanel(net, positiveWin);
    this.startResultSequence();
  }

  // =================================================================
  // Player actions
  // =================================================================

  tryPlaceBet(bet: BetType): void {
    const s = this.d.state;
    if (s.phase !== 'betting') return;
    if (s.isMultiplayer && !s.multiplayerCanBet) return;

    const chip = s.selectedChipValue;
    const chipIndex = s.selectedChipIndex;
    const ok = s.placeBet(bet, chip);
    if (!ok) {
      debugLog('[Orchestrator] placeBet rejected', bet, chip);
      return;
    }
    const tap = this.d.getLastTap();
    // Visual chip flies in immediately and the available balance drops at
    // once (req: balance reflects the bet immediately). Bets are NOT sent to
    // the backend here — single-player submits on Play (confirm) and
    // multiplayer submits when the bet countdown ends — so they stay locally
    // clearable until then (req: player can still clear the bets).
    this.d.chips.placeChip(bet.id, chip, chipIndex, tap);
  }

  clearAll(): void {
    const s = this.d.state;
    if (s.phase !== 'betting') return;
    // In multiplayer, once the countdown ends we auto-submit and lock the
    // bets — disallow clearing past that point.
    if (s.isMultiplayer && !s.multiplayerCanBet) return;
    s.clearBets();
    this.d.chips.hardClear();
  }

  async confirm(): Promise<void> {
    if (this.d.state.phase !== 'betting') return;
    if (this.d.state.isMultiplayer) return;

    const bets = this.d.state.bets.toBetLines();
    if (bets.length === 0) return;

    this.d.state.setPhase('bettingClosed');
    this.d.state.setPhase('waitingResult');

    try {
      const sid = this.d.api.getCredentials().gameSessionId ?? '';
      const result = await this.d.api.placeBet({
        sessionId: sid,
        bets,
        gid: this.gid || sid,
      });

      const parsed = parseResultString(result.result);
      this.gid = result.nextRoundId || this.gid;

      const totalBet = this.d.state.bets.total;
      const betDetails = this.d.state.bets.toDetailsString();
      const balanceBefore = this.d.state.balance;

      const round: GameRound = {
        roundId: this.gid,
        nextRoundId: result.nextRoundId,
        dice1: parsed.dice1,
        dice2: parsed.dice2,
        dice3: parsed.dice3,
        total: parsed.total,
        isTriple: parsed.isTriple,
        category: parsed.category,
        totalWinLose: result.totalWinLose,
        totalBet,
        positiveWinAmount: result.positiveWinAmount,
        hasPositiveWin: result.hasPositiveWin,
      };
      this.d.state.applyRoundResult(round);
      // Defer the balance update to the YOU WIN / YOU LOSE reveal so the
      // displayed balance stays "bet deducted" through the dice reveal and
      // only reflects winnings when the result is shown (req 2).
      this.pendingSettleBalance = result.afterBalance;

      GameLogManager.add({
        betAmount: totalBet,
        betDetails,
        winLoseAmount: result.totalWinLose,
        balanceBefore,
        balanceAfter: result.afterBalance,
        gameResult: formatDiceResult(parsed),
        roundId: this.gid,
      });

      // Show noMoreBet panel, transition to diceRoll phase. The actual
      // dice-bowl reveal kicks off either when Rive emits UIButtonClick
      // {parts: 'noMoreBet_End'} or via the safety-net timer below.
      this.d.state.setPhase('diceRoll');
      this.d.state.setShowNoMoreBetPanel(true);
      this.d.resultPanel.show('noMoreBet');
      this.d.hud.setStatus('No more bets');

      // Safety-net: if Rive event never fires, force-start the dice roll
      // 8 s after confirm (matches game_screen.dart fallback).
      this.scheduleTimer(() => {
        if (this.didStartDiceRoll) return;
        this.didStartDiceRoll = true;
        const r = this.d.state.currentRound;
        if (r) void this.playDiceRollSequence(r);
      }, 3500);
    } catch (e) {
      error('[Orchestrator] placeBet failed:', e);
      const msg = extractMessage(e);
      if (e instanceof ApiError && e.unauthorized) {
        this.d.state.setError('Session expired. Please reload.');
      } else {
        this.d.state.setError(msg);
      }
      this.d.hud.setStatus('ERROR: ' + msg);
    }
  }

  /**
   * Submit the locally-accumulated bets to the multiplayer hub. Invoked when
   * the bet countdown ends (and, as a fallback, on the server's EndBet) —
   * the multiplayer analog of single-player confirm().
   *
   * One PlaceBet is sent per INDIVIDUAL chip carrying that chip's amount; the
   * server adds each amount to the player's stake, so the per-chip increments
   * sum to the same total. We must NOT batch the per-area total — the hub
   * validates each amount against the configured chip denominations and
   * rejects a summed total (e.g. 300) with "Invalid Chip Amount"; hence
   * toChipLines() rather than toBetLines(). Idempotent: the didSubmitMpBets
   * guard prevents a double-submit when both the countdown tick and EndBet
   * fire.
   */
  private submitMultiplayerBets(): void {
    const s = this.d.state;
    if (!s.isMultiplayer) return;
    if (this.didSubmitMpBets) return;
    this.didSubmitMpBets = true;
    // Lock betting locally so no further chips/clears slip in after submit.
    s.setMultiplayerCanBet(false);

    const lines = s.bets.toChipLines();
    debugLog(
      '[Orchestrator] submitMultiplayerBets — submitting',
      lines.length,
      'chip line(s):',
      lines,
    );
    if (lines.length === 0) return;
    for (const line of lines) {
      void this.d.hub.placeBet(line.betItemId, line.amount);
    }
  }

  // =================================================================
  // Phase / animation orchestration
  // =================================================================

  /**
   * Begin a new betting round. Mirrors GamePhase.betting branch in
   * game_screen.dart:531-578.
   */
  private startBettingRound(): void {
    this.cancelTimers();
    this.roundToken++;
    this.didStartDiceRoll = false;
    this.didStartResultSequence = false;
    this.didResetAfterResult = false;
    this.didHandleResultStop = false;
    this.didRevealWinLose = false;
    this.pendingSettleBalance = null;
    this.didSettle = false;
    this.awaitingSettleBalance = false;
    this.didApplyResultSettlement = false;
    this.didSubmitMpBets = false;

    this.d.state.resetForNewRound();
    this.d.chips.hardClear();
    this.d.chips.setOpacity(1);
    this.d.state.setPhase('betting');

    this.d.rive.setBalanceText(formatCurrency(this.d.state.availableBalance));
    this.d.rive.triggerGirlAnim(1);
    this.d.rive.playDiceBowlStart();

    // In single-player only, show the startBet panel.
    if (!this.d.state.isMultiplayer) {
      this.d.state.setShowStartBetPanel(true);
      this.d.resultPanel.show('startBet');
    }

    this.d.hud.setStatus('Place your bets');
  }

  /**
   * Dice-bowl reveal. Mirrors _playDiceRollSequence (game_screen.dart:662-694).
   *   1) revealDiceBowl(dice values) — fires Rive trigger
   *   2) the bowl-open animation plays for DICE_REVEAL_DURATION_MS
   *   3) SINGLE-PLAYER only: reveal the result now that the dice have settled —
   *      confirm() already knows the win/lose amount. MULTIPLAYER does NOT reveal
   *      here; the authoritative settlement arrives asynchronously and
   *      handleResult drives the reveal on Result arrival.
   */
  private async playDiceRollSequence(round: GameRound): Promise<void> {
    this.d.rive.revealDiceBowl(round.dice1, round.dice2, round.dice3);
    await wait(DICE_REVEAL_DURATION_MS);
    if (this.d.state.isMultiplayer) return;
    const r = this.d.state.currentRound;
    this.revealResult(r?.totalWinLose ?? 0, r?.positiveWinAmount ?? 0);
  }

  /**
   * Result sequence. Mirrors game_screen.dart `_startResultSequence`:
   *   t=2s : animate chips by winMode (player/dealer/half-half)
   *   t=3s : phase=showResult; show win/lose panel
   *   t=11s: safety-net reset → new round
   */
  private startResultSequence(): void {
    if (this.didStartResultSequence) return;
    // Bail BEFORE latching the guard / cancelling timers: if the round data
    // isn't ready yet (no StartPlay, a reordered/replayed event), latching here
    // would permanently wedge the sequence — the t=3s settle/reset timers would
    // never be scheduled, so the round's win/loss would silently never apply.
    // Leaving the guard unset lets a later trigger start it once the round
    // exists.
    const round = this.d.state.currentRound;
    if (!round) return;
    this.didStartResultSequence = true;
    this.cancelTimers();

    // t=2s — chip animation
    this.scheduleTimer(() => {
      // Read the round LIVE. In multiplayer the sequence can start (via Rive
      // evtResultStop / the dice-roll safety net) before the server's Result
      // event applies the win/lose amount, and applyRoundResult swaps in a NEW
      // round object — so the `round` captured at the top of this method can be
      // stale (totalWinLose still 0). Re-reading currentRound here picks up the
      // settled amount; falls back to the captured round if it's been cleared.
      const r = this.d.state.currentRound ?? round;
      // Chip animation follows the SAME "won any bet" decision as the panel
      // (resolveWinLoseDisplay): a winning bet flies its chips to the player
      // even when the round's net is a loss, so the chips stay consistent with
      // the YOU WIN panel instead of all sweeping to the dealer.
      const mode = resolveWinLoseDisplay(r.totalWinLose, r.positiveWinAmount).mode;
      if (mode === 'win') {
        const winningIds = winningBetIds({
          dice1: r.dice1,
          dice2: r.dice2,
          dice3: r.dice3,
          total: r.total,
          isTriple: r.isTriple,
          category: r.category,
        });
        this.d.chips.animateWinningChips(winningIds);
      } else if (mode === 'lose') {
        this.d.chips.animateLosingChips();
      } else {
        this.d.chips.animateTie();
      }
      // Fade local chip layer so end-of-round chip animation doesn't
      // visually double with the leftover sprites.
      this.scheduleTimer(() => this.d.chips.setOpacity(0), 800);
    }, 2000);

    // t=3s — fallback/cleanup. revealResult() already showed YOU WIN / YOU LOSE
    // off the settled amount before starting this sequence, so the reveal here
    // is a guarded no-op in the normal case. Its real job now is the no-bet /
    // push path: when there's nothing to show, mark the result phase and let the
    // round reset.
    this.scheduleTimer(() => {
      this.d.state.setPhase('showResult');
      const r = this.d.state.currentRound ?? round;
      const display = resolveWinLoseDisplay(r.totalWinLose, r.positiveWinAmount);
      if (display.mode === 'push') {
        // Push / no-bet rounds never show the YOU WIN / YOU LOSE panel
        // (revealWinLosePanel early-returns on a push), so this is their settle
        // moment. Capture the stake FIRST — applySettlement clears the bets,
        // and the no-stake check below must see the pre-settle total.
        const hadStake = this.d.state.bets.total > 0;
        this.applySettlement();
        // Genuine push / no-bet → reset promptly. (In MP a real win/lose is
        // already revealed by revealResult; this branch is the no-stake case.)
        if (!this.d.state.isMultiplayer || !hadStake) {
          this.scheduleTimer(() => this.resetToBettingIfNeeded(), 3000);
        }
        return;
      }
      this.revealWinLosePanel(r.totalWinLose, r.positiveWinAmount);
    }, 3000);

    // t=11s — safety-net reset (matches game_screen.dart timing).
    this.scheduleTimer(() => this.resetToBettingIfNeeded(), 8000);
  }

  /**
   * Show the YOU WIN / YOU LOSE panel for the settled round.
   *
   * The win/lose decision is "did the player win ANY bet", NOT the net: a
   * winning bet shows YOU WIN even when the round's net is a loss, and the
   * amount shown is the gross winnings on the winning bets (positiveWin), not
   * the net (see resolveWinLoseDisplay). Example from the SicBo hub — Small
   * pays +98, +98 while Big/Triples lose more (net −604) → "YOU WIN 196".
   *
   * Idempotent via `didRevealWinLose`. A push (no winning bet AND no net loss)
   * shows no panel and deliberately does NOT set the guard, so a later
   * non-zero call (the async multiplayer Result) can still reveal it.
   */
  private revealWinLosePanel(net: number, positiveWin: number): void {
    if (this.didRevealWinLose) return;
    const display = resolveWinLoseDisplay(net, positiveWin);
    if (display.mode === 'push') return;
    this.didRevealWinLose = true;

    // Reflect the post-round balance now that the result is shown (req 2).
    // Clears the resolved bets in the same commit so the available balance
    // lands exactly on the server's settled value. NOTE: the wallet still
    // moves by the TRUE net — only the panel celebrates the gross win.
    this.applySettlement();

    const won = display.mode === 'win';
    this.d.state.setShowStartBetPanel(false);
    this.d.state.setShowNoMoreBetPanel(false);
    // Two-pass emit to prevent a mid-animation panel-type swap
    // (mirrors _handleResult in game_bloc.dart).
    this.d.state.setShowWinPanel(false);
    this.d.state.setShowLosePanel(false);
    this.d.state.setShowWinPanel(won);
    this.d.state.setShowLosePanel(!won);

    this.d.resultPanel.show(won ? 'win' : 'lose', display.amount);
    this.d.hud.setStatus(
      won
        ? `YOU WIN ${formatCurrency(display.amount)}`
        : `YOU LOSE ${formatCurrency(display.amount)}`,
    );
    this.d.state.setPhase('result');
  }

  /**
   * Apply the deferred balance settlement: set the wallet to the server's
   * post-round value and clear the resolved bets (atomically, to avoid a
   * one-frame flash of an intermediate available balance). Idempotent — safe
   * to call from the win/lose reveal, the push/no-bet path, and the teardown
   * safety net. If the server balance isn't known yet (a late multiplayer
   * Result), the bets are held in place and handleResult finalizes the
   * moment it arrives.
   */
  private applySettlement(): void {
    if (this.didSettle) return;
    if (this.pendingSettleBalance != null) {
      // Server's post-round balance is known — finalize atomically.
      this.didSettle = true;
      this.d.state.settle(this.pendingSettleBalance);
    } else {
      // Multiplayer: the reveal reached the settle point but the server's
      // Result event (carrying the post-round balance) hasn't arrived yet.
      // Do NOT clear the bets here — the displayed balance is
      // `wallet − pending bets`, so clearing the bets while the wallet still
      // holds the pre-settlement value would make the balance jump UP to the
      // full (pre-bet) wallet for a moment (the "balance flashes to the old
      // value" bug). Instead, leave the bets so the display stays
      // bet-deducted, and let handleResult finalize atomically the instant
      // the balance arrives.
      this.awaitingSettleBalance = true;
    }
  }

  /**
   * Tear-down between rounds. Mirrors _resetToBettingIfNeeded in
   * game_screen.dart:375-391:
   *   - hide win/lose & noMoreBet panels
   *   - clear chips
   *   - dispatch StartNewRound (→ phase=betting)
   * Idempotent via didResetAfterResult flag.
   */
  private resetToBettingIfNeeded(): void {
    if (this.didResetAfterResult) return;
    const p = this.d.state.phase;
    if (p !== 'result' && p !== 'showResult' && p !== 'diceRoll') return;
    this.didResetAfterResult = true;

    // Safety net: ensure the balance was settled even if the win/lose
    // reveal was skipped (e.g. the t=8s teardown fired first).
    this.applySettlement();

    this.cancelTimers();

    this.d.resultPanel.hide();
    this.d.state.setShowWinPanel(false);
    this.d.state.setShowLosePanel(false);
    this.d.state.setShowNoMoreBetPanel(false);

    // 1s pause, then new betting round (matches the Flutter delay).
    this.scheduleTimer(() => {
      this.d.chips.hardClear();
      this.d.chips.setOpacity(1);
      if (this.d.state.isMultiplayer) {
        // In multiplayer, the server controls round transitions — we just
        // wait for StartGame / StartBet.
        this.d.state.resetForNewRound();
        this.d.state.setPhase('newRound');
      } else {
        this.startBettingRound();
      }
    }, 1000);
  }

  // =================================================================
  // State-change → output sync
  // =================================================================

  private onStateChange(next: GameStateSnapshot, prev: GameStateSnapshot): void {
    if (next.balance !== prev.balance) {
      this.renderBalance();
    }

    if (next.bets !== prev.bets) {
      this.d.hud.setBetTotal(next.bets.total);
      // Placing or clearing a bet changes the available balance — reflect
      // it immediately (req: balance updates the instant a bet is placed).
      this.renderBalance();
      this.d.controls.setControlsEnabled(this.canInteract(next), !next.bets.isEmpty);
    }

    if (next.selectedChipIndex !== prev.selectedChipIndex) {
      this.d.controls.setSelectedChip(next.selectedChipIndex);
    }

    if (
      next.phase !== prev.phase ||
      next.multiplayerCanBet !== prev.multiplayerCanBet
    ) {
      this.d.controls.setControlsEnabled(this.canInteract(next), !next.bets.isEmpty);
    }

    if (next.config !== prev.config) {
      this.d.controls.bindChips(next.config);
      this.d.controls.setSelectedChip(next.selectedChipIndex);
    }

    if (
      next.multiplayerBetSeconds !== prev.multiplayerBetSeconds ||
      next.multiplayerRestartSeconds !== prev.multiplayerRestartSeconds
    ) {
      if (next.multiplayerBetSeconds != null) {
        this.d.timer.showBetTimer(next.multiplayerBetSeconds);
      } else if (next.multiplayerRestartSeconds != null) {
        this.d.timer.showRestartTimer(next.multiplayerRestartSeconds);
      } else {
        this.d.timer.showBetTimer(null);
      }
    }
  }

  /** Push the available balance (wallet − pending bets) to HUD + Rive. */
  private renderBalance(): void {
    const avail = this.d.state.availableBalance;
    this.d.hud.setBalance(avail);
    this.d.rive.setBalanceText(formatCurrency(avail));
  }

  /**
   * Whether chip / play / clear controls should be live. Single-player: the
   * betting phase. Multiplayer: the hub's StartBet/EndBet and our
   * countdown-end submit gate it via `multiplayerCanBet`.
   */
  private canInteract(s: GameStateSnapshot): boolean {
    return s.isMultiplayer ? s.multiplayerCanBet : s.phase === 'betting';
  }

  /** Normalise hub-event payload — see normalizePayload in MultiplayerHub. */
  private normalizeEnvelope(payload: unknown): Envelope {
    if (payload == null) return null;
    if (Array.isArray(payload)) {
      for (const item of payload) {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          const status =
            typeof payload[0] === 'string' ? (payload[0] as string) : null;
          if (status) {
            return { _status: status, ...(item as Record<string, unknown>) };
          }
          return item as Record<string, unknown>;
        }
      }
      return null;
    }
    if (typeof payload === 'object') return payload as Envelope;
    return null;
  }

  // =================================================================
  // Multiplayer
  // =================================================================

  private async startMultiplayer(): Promise<void> {
    this.d.state.setMultiplayerActive(true);
    this.d.state.setPhase('initializing');
    this.d.hud.setStatus('Connecting hub…');

    try {
      await this.d.hub.connect();

      // JoinGame sessionId is the URL-DECODED bearer token (NOT the gsid).
      const encodedToken = this.d.api.getCredentials().sessionToken ?? '';
      const sessionId = encodedToken.includes('%')
        ? decodeURIComponent(encodedToken)
        : encodedToken;
      if (!sessionId) throw new Error('Missing session token for JoinGame');

      await this.d.hub.joinGame(sessionId, GAME_KEY_MULTI);
      await this.d.hub.getMessages();
      this.d.hud.setStatus('Waiting for next round…');
    } catch (e) {
      error('[Orchestrator] multiplayer connect failed:', e);
      this.d.state.setError(extractMessage(e));
      this.d.state.setMultiplayerActive(false);
    }
  }

  private bindHubEvents(): void {
    this.d.hub.on((event) => this.handleHubEvent(event));
  }

  private handleHubEvent(event: HubEvent): void {
    const env = this.normalizeEnvelope(event.payload);

    if (env && typeof env.Index === 'number') {
      const idx = env.Index;
      if (this.lastMpEventIndex != null && idx <= this.lastMpEventIndex) return;
      this.lastMpEventIndex = idx;
    }
    // The server's Index counter RESETS to 1 every round. When we see an
    // event with State='restart' we MUST clear `lastMpEventIndex` so the
    // next round's StartGame {Index:1} / StartBet {Index:2} aren't dropped by
    // the dedup check above. The SicBo hub sends this value with mixed casing
    // (e.g. 'Restart'), so compare case-insensitively — mirrors the V2 bloc
    // (game_bloc.dart:599,865 toLowerCase) and amg_dragontiger_pixi. A
    // case-sensitive `=== 'restart'` here never matched, so the counter was
    // never reset and every event after round 1 was silently dropped (no bowl
    // shake, can't bet, no start/no-more/win/lose panels).
    if (env && String(env.State ?? '').toLowerCase() === 'restart') {
      this.lastMpEventIndex = null;
    }

    switch (event.name) {
      case '_connection':
        debugLog('[Hub] connection state', event.payload);
        break;
      case 'PlayerJoined':
        this.handlePlayerJoined(env);
        break;
      case 'PlayerLeft':
        this.handlePlayerLeft(env);
        break;
      case 'StartGame':
        this.handleStartGame(env);
        break;
      case 'StartBet':
        this.handleStartBet(env);
        break;
      case 'EndBet':
        this.handleEndBet(env);
        break;
      case 'StartPlay':
        this.handleStartPlay(env);
        break;
      case 'Result':
        this.handleResult(env);
        break;
      case 'PlaceBet':
        this.handlePlaceBetAck(env);
        break;
      case 'GetMessages':
        break;
      default:
        debugLog('[Hub] unhandled event', event.name, env);
    }
  }

  private handlePlayerJoined(env: Envelope): void {
    if (env?._status === 'Fail') {
      const msg = String(env.Content ?? 'Failed to join multiplayer table');
      this.d.state.setError(msg);
      this.d.hud.setStatus('ERROR: ' + msg);
      this.stopMultiplayer();
      this.d.state.setPhase('modeSelection');
      return;
    }

    // SicBo nests config under Data.Game.{Chips,MinBet,MaxBet}.
    const data = (env?.Data ?? env?.data) as Record<string, unknown> | undefined;
    const game = (data?.Game ?? data?.game) as
      | Record<string, unknown>
      | undefined;
    if (game) {
      const chips = parseChipsField(game.Chips ?? game.chips);
      const minBet = asNumber(game.MinBet ?? game.minBet);
      const maxBet = asNumber(game.MaxBet ?? game.maxBet);
      this.d.state.setConfig({
        ...this.d.state.config,
        chipValues: chips.length > 0 ? chips : [...this.d.state.config.chipValues],
        minBet: minBet > 0 ? minBet : this.d.state.config.minBet,
        maxBet: maxBet > 0 ? maxBet : this.d.state.config.maxBet,
      });
    }
  }

  private handlePlayerLeft(env: Envelope): void {
    const msg = String(env?.Content ?? 'You have been disconnected.');
    this.d.state.setPlayerKickedMessage(msg);
    this.stopMultiplayer();
    this.d.state.setPhase('modeSelection');
    this.d.hud.setStatus('Session ended — please reload');
  }

  private handleStartGame(_env: Envelope): void {
    this.cancelBetCountdown();
    this.cancelRestartCountdown();
    this.roundToken++;
    this.didStartDiceRoll = false;
    this.didStartResultSequence = false;
    this.didResetAfterResult = false;
    this.didHandleResultStop = false;
    this.didRevealWinLose = false;
    this.pendingSettleBalance = null;
    this.didSettle = false;
    this.awaitingSettleBalance = false;
    this.didApplyResultSettlement = false;
    this.didSubmitMpBets = false;

    this.d.state.setMultiplayerCanBet(false);
    this.d.state.setMultiplayerBetSeconds(null);
    this.d.state.setMultiplayerRestartSeconds(null);
    this.d.state.resetForNewRound();
    this.d.chips.hardClear();
    this.d.chips.setOpacity(1);
    this.d.resultPanel.hide();
    this.d.state.setPhase('newRound');
  }

  private handleStartBet(env: Envelope): void {
    const seconds = secondsBetween(env?.StartTime, env?.EndTime);
    // Fresh bet window — allow a new batched submission this round.
    this.didSubmitMpBets = false;
    this.d.state.setPhase('betting');
    this.d.state.setMultiplayerCanBet(true);
    this.d.state.setShowStartBetPanel(true);
    this.d.state.setShowNoMoreBetPanel(false);
    this.d.state.setShowWinPanel(false);
    this.d.state.setShowLosePanel(false);
    this.d.resultPanel.show('startBet');
    this.d.rive.triggerGirlAnim(1);
    this.d.rive.playDiceBowlStart();
    this.d.hud.setStatus('Place your bets');
    // End the visible countdown BET_SUBMIT_BUFFER_SEC seconds early so the
    // batched submit (at countdown 0) reaches the backend before the real
    // window closes and play starts.
    this.startBetCountdown(Math.max(0, seconds - BET_SUBMIT_BUFFER_SEC));
  }

  private handleEndBet(_env: Envelope): void {
    this.cancelBetCountdown();
    // Submit now if the local countdown didn't already (server closed the
    // window first). Idempotent via didSubmitMpBets.
    this.submitMultiplayerBets();
    this.d.state.setMultiplayerCanBet(false);
    this.d.state.setMultiplayerBetSeconds(null);
    this.d.state.setPhase('bettingClosed');
    this.d.state.setShowStartBetPanel(false);
    this.d.state.setShowNoMoreBetPanel(true);
    this.d.resultPanel.show('noMoreBet');
    this.d.hud.setStatus('No more bets');
  }

  private handleStartPlay(env: Envelope): void {
    const raw = String(env?.Result ?? env?.result ?? '');
    if (!raw) return;
    let parsed: ParsedResult;
    try {
      parsed = parseResultString(raw);
    } catch (e) {
      warn('[Hub] StartPlay parse failed:', e, raw);
      return;
    }
    this.d.state.setMultiplayerResultString(raw);
    this.d.state.applyRoundResult({
      roundId: '',
      nextRoundId: '',
      dice1: parsed.dice1,
      dice2: parsed.dice2,
      dice3: parsed.dice3,
      total: parsed.total,
      isTriple: parsed.isTriple,
      category: parsed.category,
      totalWinLose: 0, // arrives via Result event
      totalBet: this.d.state.bets.total,
      positiveWinAmount: 0,
      hasPositiveWin: false,
    });

    if (!this.didStartDiceRoll) {
      this.didStartDiceRoll = true;
      this.d.state.setPhase('diceRoll');
      const r = this.d.state.currentRound;
      if (r) void this.playDiceRollSequence(r);
    }
  }

  private handleResult(env: Envelope): void {
    const state = String(env?.State ?? '').toLowerCase();

    // Restart announcement → start the next-round countdown. Keyed SOLELY off
    // State='restart' (mirrors the V2 bloc _handleResult, game_bloc.dart:865,
    // which tells restart apart from settlement by State — never by the mere
    // presence of timestamps). The SicBo settlement Result ALSO carries
    // StartTime/EndTime, so the old `|| env?.StartTime || env?.EndTime`
    // condition misread every settlement as a restart and returned early —
    // that is why YOU WIN / YOU LOSE never showed and the bet log stayed empty
    // (we bailed before applyRoundResult + GameLogManager.add below).
    if (state === 'restart') {
      const seconds = secondsBetween(env?.StartTime, env?.EndTime);
      if (seconds > 0) {
        this.d.state.setMultiplayerGameState('restart');
        this.startRestartCountdown(seconds);
      }
      return;
    }

    // Settlement requires a per-user Data payload (game_bloc.dart:896). A
    // Result with no Data is info-only — nothing to settle.
    if (env?.Data == null) return;

    // Settle off the AUTHORITATIVE post-round balance. The backend stopped
    // returning a usable per-bet figure (WinLost is 0 even on a loss) and the
    // Result event no longer carries a per-user Balance, so the wallet can't be
    // derived locally — we read it from the server (/action/balance). Async.
    void this.settleFromResult(env);
  }

  /**
   * Resolve the player's authoritative post-round balance, then run settlement.
   *
   * Balance source, in order of preference:
   *   1. The Result event's per-user `Balance` field — used when the server
   *      still sends it.
   *   2. /action/balance (checkBalance) — the backend now omits Balance from the
   *      Result event AND returns WinLost=0 even on a loss, so neither the wallet
   *      nor the net P&L can be derived locally; the only correct source is the
   *      server's own balance. NOTE: /action/getinfo does NOT carry a balance
   *      (it returns config only), so we must use /action/balance, the same
   *      endpoint the startup balance fetch uses.
   *
   * Async because of the balance round-trip, so it captures the round token up
   * front and bails if a new round started before the balance arrived — a slow
   * fetch must never settle a finished round into the next round's wallet.
   */
  private async settleFromResult(env: Envelope): Promise<void> {
    const token = this.roundToken;
    const userName = (this.d.api.getCredentials().userName || '').toLowerCase();
    const winLose = extractWinLoseForUser(env?.Data, userName);
    // Gross winnings on the bets this user WON (sum of positive WinLost only) —
    // drives the "won any bet → YOU WIN" display, independent of the net.
    const positiveWin = extractPositiveWinForUser(env?.Data, userName);
    const betDetailsStr = extractBetDetails(env?.Data, userName);
    let balance = extractBalanceForUser(env?.Data, userName);

    if (balance == null) {
      // Server didn't include the post-round balance — fetch it from
      // /action/balance.
      balance = await this.fetchLatestBalance();
    }

    if (token !== this.roundToken) {
      debugLog(
        '[Hub] Result settlement dropped — round advanced during balance fetch',
      );
      return;
    }

    this.applyResultSettlement(winLose, positiveWin, balance, betDetailsStr);
  }

  /**
   * Fetch the latest wallet balance from the backend via /action/balance
   * (checkBalance) — the same endpoint the startup balance fetch uses.
   * (/action/getinfo carries config only, no balance.) Returns null on failure
   * so the caller can fall back to the locally-tracked wallet rather than
   * zeroing the balance on a transient network error.
   */
  private async fetchLatestBalance(): Promise<number | null> {
    try {
      const sessionId = this.d.api.getCredentials().gameSessionId ?? '';
      const balance = await this.d.api.checkBalance({ sessionId });
      debugLog('[Hub] /action/balance post-result balance =', balance);
      return balance;
    } catch (e) {
      warn('[Hub] checkBalance after result failed — keeping local balance:', e);
      return null;
    }
  }

  /**
   * Apply the resolved post-round balance: settle the wallet, drive the
   * YOU WIN / YOU LOSE panel, and log the round. `balance` is the authoritative
   * post-round wallet (from the Result event or /action/balance); null only
   * when both sources failed, in which case we fall back to
   * `balanceBefore + winLose` (the legacy local computation).
   *
   * Once per round — a retransmitted settlement Result must not re-run this
   * (see didApplyResultSettlement); a second pass would read the already-settled
   * wallet as `balanceBefore` and double-log.
   */
  private applyResultSettlement(
    winLose: number,
    positiveWin: number,
    balance: number | null,
    betDetailsStr: string,
  ): void {
    if (this.didApplyResultSettlement) return;
    this.didApplyResultSettlement = true;

    const totalBet = this.d.state.bets.total;
    const betDetails = betDetailsStr || this.d.state.bets.toDetailsString();
    const balanceBefore = this.d.state.balance;
    const balanceAfter = balance ?? balanceBefore + winLose;

    this.d.state.setMultiplayerWinLose(winLose);
    // Defer the balance update to the YOU WIN / YOU LOSE reveal, like
    // single-player (req 2) — revealResult below drives the settle the
    // moment the panel shows.
    this.pendingSettleBalance = balanceAfter;
    if (this.awaitingSettleBalance && !this.didSettle) {
      // The reveal already reached the settle point and was only waiting on
      // this balance — finalize now (atomic balance update + bet clear).
      // Settling atomically here avoids clearing bets early (which would
      // flash the pre-settlement wallet).
      this.didSettle = true;
      this.awaitingSettleBalance = false;
      this.d.state.settle(balanceAfter);
    } else if (this.didSettle) {
      // Settlement already finalized (a late / duplicate Result) — re-sync
      // the wallet directly; the bets were already cleared.
      this.d.state.setBalance(balanceAfter);
    }

    const cur = this.d.state.currentRound;
    if (cur) {
      this.d.state.applyRoundResult({
        ...cur,
        totalWinLose: winLose,
        positiveWinAmount: positiveWin,
        hasPositiveWin: positiveWin > 0,
      });
    }

    // Record the round in the bet log on settlement. Done OUTSIDE the `if (cur)`
    // guard so a missed StartPlay (currentRound still null) doesn't also drop
    // the log entry — the win/lose + balance come from the Result payload, not
    // the round. Skip rounds the player sat out (no stake, no win/lose) so the
    // log isn't spammed with empty "No bet" rows.
    const participated =
      totalBet > 0 || winLose !== 0 || betDetails.length > 0;
    if (participated) {
      GameLogManager.add({
        betAmount: totalBet,
        betDetails,
        winLoseAmount: winLose,
        balanceBefore,
        balanceAfter,
        gameResult: cur
          ? formatDiceResult({
              dice1: cur.dice1,
              dice2: cur.dice2,
              dice3: cur.dice3,
              total: cur.total,
              isTriple: cur.isTriple,
              category: cur.category,
            })
          : this.d.state.multiplayerResultString ?? '',
        roundId: cur?.roundId || null,
      });
    }

    // Reveal YOU WIN / YOU LOSE on Result arrival — this is the moment the
    // authoritative settlement amount is known (the dice/bowl reveal alone does
    // not carry it). Drives the girl reaction, the win/lose panel, and the
    // chip-animation / reset sequence. Mirrors V2 _handleResult, which shows the
    // panel on settlement. Idempotent per round.
    this.revealResult(winLose, positiveWin);
  }

  /**
   * PlaceBet ack — server confirmed (part of) our batched bet submission.
   *
   * No-op for balance: bets are submitted as a single batch when the
   * countdown ends and the displayed balance is optimistic (wallet −
   * pending bets). The authoritative wallet is re-synced from the Result
   * event at settlement, so applying the ack's per-bet balance here would
   * double-count the deduction.
   */
  private handlePlaceBetAck(_env: Envelope): void {
    // intentionally empty (see above)
  }

  // -----------------------------------------------------------------
  // Multiplayer countdown timers
  // -----------------------------------------------------------------

  private startBetCountdown(seconds: number): void {
    this.cancelBetCountdown();
    let remaining = Math.max(0, Math.floor(seconds));
    this.d.state.setMultiplayerBetSeconds(remaining);
    this.betCountdownHandle = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        this.cancelBetCountdown();
        this.d.state.setMultiplayerBetSeconds(null);
        // Countdown ended — submit the batched bets now (req 3: multiplayer
        // submits when the countdown ends, not per-chip).
        this.submitMultiplayerBets();
      } else {
        this.d.state.setMultiplayerBetSeconds(remaining);
      }
    }, 1000);
  }

  private cancelBetCountdown(): void {
    if (this.betCountdownHandle != null) {
      window.clearInterval(this.betCountdownHandle);
      this.betCountdownHandle = null;
    }
  }

  private startRestartCountdown(seconds: number): void {
    this.cancelRestartCountdown();
    let remaining = Math.max(0, Math.floor(seconds));
    this.d.state.setMultiplayerRestartSeconds(remaining);
    this.restartCountdownHandle = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        this.cancelRestartCountdown();
        this.d.state.setMultiplayerRestartSeconds(null);
      } else {
        this.d.state.setMultiplayerRestartSeconds(remaining);
      }
    }, 1000);
  }

  private cancelRestartCountdown(): void {
    if (this.restartCountdownHandle != null) {
      window.clearInterval(this.restartCountdownHandle);
      this.restartCountdownHandle = null;
    }
  }

  private stopMultiplayer(): void {
    this.cancelBetCountdown();
    this.cancelRestartCountdown();
    void this.d.hub.disconnect();
    this.d.state.setMultiplayerActive(false);
  }

  // =================================================================
  // Helpers
  // =================================================================

  private scheduleTimer(fn: () => void, ms: number): void {
    const id = window.setTimeout(fn, ms);
    this.sequenceTimers.push(id);
  }

  private cancelTimers(): void {
    for (const id of this.sequenceTimers) window.clearTimeout(id);
    this.sequenceTimers = [];
  }
}

// =====================================================================

function wait(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

function formatCurrency(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

/**
 * Decide how a settled round is PRESENTED (YOU WIN / YOU LOSE panel, status
 * text, chip animation). The decision is "did the player win ANY bet", not the
 * net across all bets:
 *
 *   positiveWin > 0 → win,  amount = positiveWin (gross winnings on won bets)
 *   else net < 0    → lose, amount = −net
 *   else            → push  (no winning bet AND no net loss → no panel)
 *
 * So a player who wins one area but loses more elsewhere still sees YOU WIN for
 * the amount they won. Example from the SicBo hub: Small pays +98, +98 while
 * Big/Triples lose more (net −604) → { win, 196 }.
 *
 * This affects DISPLAY only — balance settlement still moves the wallet by the
 * true net (via the server's post-round balance in applySettlement), and the
 * bet log still records the net win/lose.
 */
function resolveWinLoseDisplay(
  net: number,
  positiveWin: number,
): { mode: 'win' | 'lose' | 'push'; amount: number } {
  if (positiveWin > 0) return { mode: 'win', amount: positiveWin };
  if (net < 0) return { mode: 'lose', amount: -net };
  return { mode: 'push', amount: 0 };
}

function ensureUrlEncoded(token: string): string {
  if (!token) return token;
  if (token.includes('%')) return token;
  return encodeURIComponent(token);
}

function parseGameUrl(
  currentGameUrl: string,
  loginToken: string,
): { gameToken: string; gsid: string } {
  if (!currentGameUrl) return { gameToken: loginToken, gsid: '' };
  try {
    const u = new URL(currentGameUrl, 'https://placeholder.invalid');
    const urlToken = u.searchParams.get('token');
    const urlGsid = u.searchParams.get('gsid');
    return {
      gameToken: urlToken && urlToken.length > 0 ? urlToken : loginToken,
      gsid: urlGsid ?? '',
    };
  } catch {
    return { gameToken: loginToken, gsid: '' };
  }
}

function extractMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  try {
    return JSON.stringify(e);
  } catch {
    return String(e);
  }
}

// =====================================================================
// Envelope helpers
// =====================================================================

type Envelope = Record<string, unknown> | null;

function asNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function parseChipsField(raw: unknown): number[] {
  const out: number[] = [];
  if (typeof raw === 'string') {
    for (const piece of raw.split(',')) {
      const n = parseInt(piece.trim(), 10);
      if (Number.isFinite(n) && n > 0) out.push(n);
    }
  } else if (Array.isArray(raw)) {
    for (const v of raw) {
      const n = typeof v === 'number' ? v : parseInt(String(v), 10);
      if (Number.isFinite(n) && n > 0) out.push(n);
    }
  }
  return out;
}

function secondsBetween(start: unknown, end: unknown): number {
  if (typeof start !== 'string' || typeof end !== 'string') return 0;
  const a = Date.parse(start);
  const b = Date.parse(end);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.round((b - a) / 1000);
}

/**
 * Sum the current user's WinLost out of a Result envelope's Data array.
 * SicBo's canonical field is `WinLost` (note the spelling) but we accept
 * all four variants.
 */
function extractWinLoseForUser(data: unknown, userNameLower: string): number {
  if (data == null || !userNameLower) return 0;

  if (Array.isArray(data)) {
    let sum = 0;
    for (const raw of data) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      const u = pickUserName(item);
      // Skip broadcast/empty-username rows AND rows for other players.
      if (!u || u !== userNameLower) continue;
      sum += asNumber(
        item.WinLost ?? item.WinLose ?? item.winLost ?? item.winLose,
      );
    }
    return sum;
  }
  if (typeof data === 'object') {
    const m = data as Record<string, unknown>;
    // Single-map shape (rare): require a user match if a username is present,
    // otherwise fall back to the top-level total.
    const u = pickUserName(m);
    if (u && u !== userNameLower) return 0;
    return asNumber(m.TotalWinLose ?? m.WinLose ?? m.WinLost ?? m.winLose);
  }
  return 0;
}

/**
 * Sum ONLY the current user's POSITIVE WinLost values out of a Result
 * envelope's Data array — i.e. the gross winnings on the bets they won,
 * ignoring losing bets. Drives the "won any bet → YOU WIN" presentation
 * (resolveWinLoseDisplay), independent of the net. Filters per-user with the
 * same rules as extractWinLoseForUser (skip broadcast / other-player rows).
 */
function extractPositiveWinForUser(data: unknown, userNameLower: string): number {
  if (data == null || !userNameLower) return 0;

  if (Array.isArray(data)) {
    let sum = 0;
    for (const raw of data) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      const u = pickUserName(item);
      if (!u || u !== userNameLower) continue;
      const wl = asNumber(
        item.WinLost ?? item.WinLose ?? item.winLost ?? item.winLose,
      );
      if (wl > 0) sum += wl;
    }
    return sum;
  }
  if (typeof data === 'object') {
    // Single-map shape: no per-bet breakdown — the only positive signal is a
    // positive total.
    const m = data as Record<string, unknown>;
    const u = pickUserName(m);
    if (u && u !== userNameLower) return 0;
    const total = asNumber(m.TotalWinLose ?? m.WinLose ?? m.WinLost ?? m.winLose);
    return total > 0 ? total : 0;
  }
  return 0;
}

/** Probe `Username` (canonical) → `username` → `UserName` → `userName`. */
function pickUserName(item: Record<string, unknown>): string {
  const raw = item.Username ?? item.username ?? item.UserName ?? item.userName;
  return raw == null ? '' : String(raw).toLowerCase();
}

/**
 * Extract the current user's Balance from a Result / PlaceBet envelope's
 * `Data` field. Ported from amg_dragontiger_pixi (same hub messages).
 *
 * Three rules that MUST be observed (each corresponds to a real balance bug
 * we hit on this server):
 *
 *   1. The primary field name is `Username` (capital-U, lower-n) — the
 *      lowercase / Pascal-case variants only exist on some events. Probe
 *      `Username` → `username` → `UserName` → `userName` (see pickUserName).
 *
 *   2. Entries with empty / missing username MUST be skipped, not matched.
 *      Hub broadcasts include "info-only" entries with no username carrying
 *      placeholder Balance values (often 0 or the dealer's running totals).
 *      Treating those as a match drops the player's balance to 0 mid-round.
 *
 *   3. If the matched entry has `Balance: null`, return null (don't update).
 *      Coercing a null/non-numeric balance to 0 (the old asNumber path) is
 *      what blanked the wallet right after the first bet.
 *
 * Both the array and single-map data shapes apply the same rules.
 */
function extractBalanceForUser(data: unknown, userNameLower: string): number | null {
  if (data == null || !userNameLower) return null;

  if (Array.isArray(data)) {
    for (const raw of data) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      const u = pickUserName(item);
      if (!u || u !== userNameLower) continue; // rule 2
      const b = item.Balance ?? item.balance;
      if (b == null) return null; // rule 3
      const n = typeof b === 'number' ? b : Number(b);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }

  if (typeof data === 'object') {
    const m = data as Record<string, unknown>;
    const u = pickUserName(m);
    if (!u || u !== userNameLower) return null; // rule 2 — never trust a no-username row
    const b = m.Balance ?? m.balance;
    if (b == null) return null; // rule 3
    const n = typeof b === 'number' ? b : Number(b);
    return Number.isFinite(n) ? n : null;
  }

  return null;
}

/** "Small:500, Total 8:200" — server-side bet log for the current user. */
function extractBetDetails(data: unknown, userNameLower: string): string {
  if (!Array.isArray(data)) return '';
  const parts: string[] = [];
  for (const raw of data) {
    if (!raw || typeof raw !== 'object') continue;
    const item = raw as Record<string, unknown>;
    const name = pickUserName(item);
    if (!name || name !== userNameLower) continue;
    const itemName = String(item.ItemName ?? item.itemName ?? '');
    const amount = asNumber(item.Amount ?? item.amount);
    if (itemName && amount > 0) {
      parts.push(`${itemName}:${Math.round(amount).toLocaleString('en-US')}`);
    }
  }
  return parts.join(', ');
}

// Silence unused-imports warnings for symbols pre-imported for clarity.
export const _UNUSED = { REF_WIDTH, REF_HEIGHT };
